// useWelcomeTour.js - Corrected to match actual WelcomePage elements
import { useTour } from './TourProvider';
import { useTranslation } from 'react-i18next';

// Tour configuration for Welcome Page - matching actual elements
export const welcomeTourSteps = [
  {
    selector: '[data-tour="welcome-logo"]',
    title: 'Welcome to Democratic Chess! 🏆',
    content: 'Welcome to Democratic Chess - the revolutionary way to play chess where strategy meets collaboration.',
    additionalInfo: 'This innovative platform combines traditional chess with modern teamwork features, creating a unique gaming experience.',
    positioning: {
      position: 'below',
      offset: 30,
      align: 'center'
    }
  },
  {
    selector: '[data-tour="features-section"]',
    title: 'Explore Amazing Features 🎮',
    content: 'Discover the unique features that make Democratic Chess special - from coin mining to skill development.',
    additionalInfo: 'Each feature is designed to enhance your chess experience and help you grow as a player.',
    positioning: {
      position: 'above',
      offset: 20,
      align: 'center'
    }
  },
  {
    selector: '[data-tour="user-stats"]',
    title: 'Track Your Progress 📊',
    content: 'Monitor your games, wins, rating, and coins all in one place.',
    additionalInfo: 'Your statistics help you understand your growth and achievements over time.',
    positioning: {
      position: 'above',
      offset: 20,
      align: 'center'
    }
  },
  {
    selector: '[data-tour="start-game"]',
    title: 'Ready to Play? 🚀',
    content: 'Click here to start your chess adventure and experience democratic gameplay!',
    additionalInfo: 'This button will take you to the main game area where you can join or create games.',
    positioning: {
      position: 'above',
      offset: 20,
      align: 'center'
    }
  },
  {
    selector: '[data-tour="tour-button"]',
    title: 'Need Help? 💡',
    content: 'Click this tour button anytime to get guidance and tips.',
    additionalInfo: 'The tour is always available to help you navigate and understand all features.',
    positioning: {
      position: 'left',
      offset: 20,
      align: 'center'
    }
  }
];

// Hook for Welcome Page tour
export const useWelcomeTour = () => {
  const { 
    startTour, 
    forceShowTour, 
    hasPermanentDisablePreference, 
    isTourClosedInSession,
    clearPermanentPreference 
  } = useTour();
  const { t } = useTranslation();
  
  const tourName = 'welcomePage';
  
  // Start tour automatically (respects user preferences and session closure)
  const startWelcomeTour = () => {
    console.log('🎓 Starting Welcome Page tour automatically...');
    
    // Map steps with translations
    const steps = welcomeTourSteps.map(step => ({
      ...step,
      title: step.title, // Using direct titles since they're already in English
      content: step.content,
      additionalInfo: step.additionalInfo,
      positioning: step.positioning
    }));
    
    // Create tour data object
    const tourData = {
      name: tourName,
      steps: steps
    };
    
    console.log('🎓 Tour data:', tourData);
    
    // Use startTour (respects preferences and session closure)
    return startTour(tourData);
  };
  
  // Force show tour (when user clicks tour button - ALWAYS shows, ignores ALL preferences)
  const forceShowWelcomeTour = () => {
    console.log('🎓 Force showing Welcome Page tour - ignoring ALL preferences...');
    
    // Map steps with translations
    const steps = welcomeTourSteps.map(step => ({
      ...step,
      title: step.title,
      content: step.content,
      additionalInfo: step.additionalInfo,
      positioning: step.positioning
    }));
    
    // Create tour data object with special flag to bypass ALL checks
    const tourData = {
      name: tourName,
      steps: steps,
      forceShow: true,
      bypassAllChecks: true // Special flag for absolute force
    };
    
    console.log('🎓 Force tour data:', tourData);
    
    // Directly set the tour state without any checks
    return forceShowTour(tourData);
  };
  
  // Check if tour should be shown automatically
  const shouldShowWelcomeTour = () => {
    // Don't show if permanently disabled
    if (hasPermanentDisablePreference(tourName)) {
      console.log('🎓 Tour permanently disabled');
      return false;
    }
    
    // Don't show if closed in this session
    if (isTourClosedInSession(tourName)) {
      console.log('🎓 Tour closed in session');
      return false;
    }
    
    console.log('🎓 Tour should show');
    return true;
  };
  
  return {
    startWelcomeTour,        // Automatic tour start (respects all preferences)
    forceShowWelcomeTour,   // Manual tour start (when user clicks tour button)
    shouldShowWelcomeTour,   // Check if tour should show automatically
    clearPermanentPreference, // Clear permanent disable preference
    tourName,
  };
};